import React, { useState, useCallback } from 'react';
import createToast from '../../../../utils/toast';
import { Button, ProgressBar } from '../../../ui-kit';
import { invertColor } from '../../../ui-kit/helpers';
import { connect } from 'react-redux';
import SettingsContent from './Settings';
import { operations as incidentOperations } from '../../../../state/modules/incidents';
import { selectors as dashboardSelectors } from '../../../../state/modules/dashboards';
import Loader from '../../../general/Loader';
import { isMobileOnly } from 'react-device-detect';
import './AnimatedProgressBar.css';

const AnimatedProgressBar = () => (
  <ProgressBar now={100} style={{ height: '1rem' }} className="animated-progress-bar" />
);

const IncidentCreationButton = ({ button, onClick, disabled, gridSize }) => {
  const [clickCount, setClickCount] = useState(0);

  const confirmMessage = I18n.t('dashboards.widget_archetypes.quick_incident_creation_buttons.click_again_to_confirm');

  const handleClick = () => {
    if (button.url) {
      // this is currently for admins only. Instead of creating an incident, a
      // button simply opens a link. This is used for creating demos by FSR
      // admins.
      window.open(button.url, '_blank');
    } else if (clickCount === 0) {
      // first click
      setClickCount(1);
      setTimeout(() => setClickCount(0), 5000);
    } else {
      // second click
      onClick(button);
      setClickCount(0);
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={disabled}
      className="m-1"
      variant="default"
      style={{
        background: button.button_color,
        aspectRatio: 1,
        // In mobile mode, since the widget's height is dynamic
        // the buttons don't have a reference to scale
        // evenly. As such, we set a minimum height to ensure
        // the buttons are not too small
        minHeight: isMobileOnly && '160px',
        fontSize: clickCount === 0 ? `calc(1.5em / ${gridSize} + 1em)` : null,
        color: invertColor(button.button_color),
        fontWeight: clickCount === 0 ? 'bolder' : ''
      }}
    >
      {clickCount === 0 && button.button_title}
      {clickCount === 1 && (
        <>
          <h4>{confirmMessage}</h4>
          <AnimatedProgressBar />
        </>
      )}
    </Button>
  );
};

const QuickIncidentCreationButtons = ({ currentGroup, buttons, createNewIncident, storeIncidentInBackend }) => {
  const [incidentBeingCreated, setIncidentBeingCreated] = useState(false);

  const onClick = useCallback(
    async button => {
      const incident = await createNewIncident({
        body: button.message,
        activity_type_ids: button.activity_type_ids,
        location: button.location,
        prio: button.prio,
        task_ids: button.task_ids,
        groups: [],
        manual: false,
        type: 'incident_alert',
        split_by_station: true,
        backup_alert: button.backup_alert,
        start_time: null, // override defaults
        end_time: null // override defaults
      });

      setIncidentBeingCreated(true);

      const response = await storeIncidentInBackend(incident.id, {
        headers: { 'Idempotency-Key': globalThis.crypto.randomUUID() }
      });

      setIncidentBeingCreated(false);

      if (response?.success)
        createToast({
          message: I18n.t('incidents.created_successfully'),
          type: 'success'
        });
    },
    [createNewIncident, storeIncidentInBackend]
  );

  if (!currentGroup) return <Loader />;
  if (!(currentGroup.enabled_features || []).includes('alerting')) return I18n.t('system_features.alerting.disabled');

  // get nearest square
  const gridSize = [1, 2, 3, 4, 5, 6, 7, 8].find(s => buttons.length <= s * s);

  return (
    <div className="h-100" style={{ display: 'grid', gridTemplateColumns: `repeat(${gridSize}, minmax(100px, 1fr))` }}>
      {buttons.map(button => (
        <IncidentCreationButton
          key={button.id}
          button={button}
          onClick={onClick}
          gridSize={gridSize}
          disabled={incidentBeingCreated}
        />
      ))}
    </div>
  );
};

const mapStateToProps = state => ({
  currentGroup: dashboardSelectors.selectCurrentDashboardGroup(state)
});

const mapDispatchToProps = {
  createNewIncident: incidentOperations.createNew,
  storeIncidentInBackend: incidentOperations.storeInBackend
};

// way of adding a static SettingsContent variable to a functional component
Object.assign(QuickIncidentCreationButtons, {
  SettingsContent,
  defaultSettings: { buttons: [] }
});

export default connect(mapStateToProps, mapDispatchToProps)(QuickIncidentCreationButtons);
